// src/pages/Settings/communication/EmailTemplate.js
import React, { useState, useEffect } from "react";
import { get } from 'helpers/api_helper';
import { Row, Col, Card, CardBody, CardHeader, Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import Datatables from 'pages/utils/table/datatable';
import { useTranslation } from "react-i18next";
import cookieHelper from "helpers/getCookieData";
import {
  RiMailLine,
  RiShieldCheckLine,
  RiEyeLine,
  RiInformationLine,
  RiCheckboxCircleLine,
  RiFileTextLine,
} from "react-icons/ri";

const EmailTemplate = () => {
  const { t } = useTranslation();
  const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await get('/notifications/global-templates?template_type=EMAIL');
      if (res?.success && res?.body) {
        setRows(res.body);
      }
    } catch (err) {
      console.warn("Notice loading Email templates:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const getRenderedPreview = (tpl) => {
    if (!tpl) return '';
    const sampleData = {
      patient_name: 'Rahul Sharma',
      doctor_name: 'Dr. Vivek Sharma',
      appointment_date: 'Tomorrow at 10:30 AM',
      clinic_name: clinic?.clinic_name || 'Oralstop Dental Clinic',
      chair_code: 'Chair #1',
      number: clinic?.phone || '+91 9090909090',
      amount: '₹ 1,200',
    };
    let text = tpl.body_text || '';
    Object.keys(sampleData).forEach((key) => {
      text = text.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'gi'), sampleData[key]);
    });
    return text;
  };

  const tableColumns = [
    {
      dataField: 'id',
      text: '#',
      style: { width: '45px', textAlign: 'center' },
      formatter: (cell, row, rowIndex) => <span className="font-weight-semibold text-muted">{rowIndex + 1}</span>,
    },
    {
      dataField: 'title',
      text: t('Template Title & Event'),
      formatter: (cell, row) => (
        <div>
          <strong className="text-dark d-block">{cell}</strong>
          <code className="text-primary font-size-11">{row.template_key}</code>
        </div>
      ),
    },
    {
      dataField: 'subject',
      text: t('Email Subject Line'),
      formatter: (cell) => (
        <span className="text-muted font-size-12">{cell || '—'}</span>
      ),
    },
    {
      dataField: 'category',
      text: t('Category'),
      formatter: (cell) => (
        <span className="badge bg-light text-dark border font-size-11">{cell || 'UTILITY'}</span>
      ),
    },
    {
      dataField: 'actions',
      text: t('Preview'),
      style: { width: '80px', textAlign: 'center' },
      formatter: (cell, row) => (
        <Button
          size="sm"
          color="outline-primary"
          onClick={() => setPreviewTemplate(row)}
          title="Preview Email Layout"
        >
          <RiEyeLine />
        </Button>
      ),
    },
  ];

  return (
    <React.Fragment>
      {/* Super Admin Managed Notice */}
      <Card className="border shadow-sm mb-3 bg-light border-start border-4 border-primary">
        <CardBody className="py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="rounded-circle p-2 bg-primary text-white font-size-18 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
              <RiShieldCheckLine />
            </div>
            <div>
              <h6 className="font-weight-bold text-dark mb-0">
                {t("Super Admin Managed Global Email Templates")}
              </h6>
              <p className="text-muted font-size-12 mb-0">
                {t("Standard email layouts are maintained platform-wide by Super Admin with responsive HTML formatting, clinic branding placeholders, and high inbox delivery rates.")}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="border shadow-sm">
        <CardBody>
          <Datatables
            isSearch={true}
            columns={tableColumns}
            showTableOnly={true}
            rowsLength={rows?.length || 0}
            rows={rows || []}
            keyField={'id'}
            title={t("Master Email Notification Templates")}
            isAdd={false}
            isTableHead={true}
            loading={loading}
            ssr={() => { }}
          />
        </CardBody>
      </Card>

      {/* Live Preview Modal */}
      <Modal isOpen={Boolean(previewTemplate)} toggle={() => setPreviewTemplate(null)} size="lg" centered>
        <ModalHeader toggle={() => setPreviewTemplate(null)}>
          <RiMailLine className="text-primary me-1 font-size-18" /> {previewTemplate?.title}
        </ModalHeader>
        <ModalBody className="p-4 bg-light">
          {previewTemplate && (
            <div className="p-4 bg-white border rounded shadow-sm">
              <div className="border-bottom pb-2 mb-3">
                <span className="text-muted font-size-12 d-block">{t("Subject Line:")}</span>
                <strong className="text-dark font-size-14">
                  {getRenderedPreview({ body_text: previewTemplate.subject })}
                </strong>
              </div>
              {previewTemplate.html_content ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: getRenderedPreview({ body_text: previewTemplate.html_content }),
                  }}
                />
              ) : (
                <div className="font-size-13" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {getRenderedPreview(previewTemplate)}
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPreviewTemplate(null)}>
            {t("Close Preview")}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default EmailTemplate;
